import { useMutation, useQuery, useQueryClient, type UseMutationOptions, type UseQueryOptions } from "@tanstack/react-query";
import { api } from "./custom-fetch";
import type {
  AcceptInviteResult,
  AiTurnList,
  AuditLog,
  AuthMeResponse,
  CreateInviteResult,
  CreateProjectInput,
  HealthStatus,
  InvitePreview,
  Project,
  ProjectFileContents,
  ProjectFileList,
  ProjectList,
  ProjectMembersResponse,
  PublishedProject,
  SharedProjectList,
} from "./types";

/**
 * Query keys are part of the public API — kept stable so consumers
 * can invalidate from anywhere.
 */
export const qk = {
  health: ["health"] as const,
  authMe: ["auth", "me"] as const,
  projects: (filter?: { limit?: number; cursor?: string }) =>
    ["projects", "list", filter ?? {}] as const,
  project: (id: string) => ["projects", id] as const,
  projectFiles: (id: string, prefix?: string) =>
    ["projects", id, "files", prefix ?? ""] as const,
  projectFile: (id: string, path: string) =>
    ["projects", id, "files", "read", path] as const,
};

// ---------- Health ----------

export function useHealth(options?: UseQueryOptions<HealthStatus>) {
  return useQuery({
    queryKey: qk.health,
    queryFn: () => api<HealthStatus>("/api/healthz"),
    staleTime: 30_000,
    ...options,
  });
}

// ---------- Auth ----------

export interface AuthProvidersResponse {
  readonly magicLink: boolean;
  readonly google: boolean;
  readonly github: boolean;
}

/** Reports which providers the API server has credentials for. UI uses
 * this to hide buttons that would 404 on click. */
export function useAuthProviders(options?: UseQueryOptions<AuthProvidersResponse>) {
  return useQuery({
    queryKey: ["auth", "providers"] as const,
    queryFn: () => api<AuthProvidersResponse>("/api/auth/providers"),
    staleTime: 60_000,
    ...options,
  });
}

export function useAuthMe(options?: UseQueryOptions<AuthMeResponse>) {
  return useQuery({
    queryKey: qk.authMe,
    queryFn: () => api<AuthMeResponse>("/api/auth/me"),
    staleTime: 60_000,
    ...options,
  });
}

export interface MagicLinkResponse {
  readonly sent: boolean;
  /** Present only when VORLOOP_EXPOSE_DEV_TOKEN=1 — never in prod. */
  readonly devUrl?: string;
}

export function useRequestMagicLink(
  options?: UseMutationOptions<MagicLinkResponse, Error, { email: string; redirectTo?: string }>,
) {
  return useMutation({
    mutationFn: (body) =>
      api<MagicLinkResponse>("/api/auth/magic-link", { method: "POST", body }),
    ...options,
  });
}

export function useSignOut(options?: UseMutationOptions<void, Error>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/api/auth/signout", { method: "POST" }),
    onSuccess: () => {
      qc.setQueryData<AuthMeResponse>(qk.authMe, { user: null });
    },
    ...options,
  });
}

// ---------- Projects ----------

export function useListProjects(
  filter?: { limit?: number; cursor?: string },
  options?: UseQueryOptions<ProjectList>,
) {
  return useQuery({
    queryKey: qk.projects(filter),
    queryFn: () => api<ProjectList>("/api/projects", { query: filter }),
    ...options,
  });
}

export function useGetProject(projectId: string, options?: UseQueryOptions<Project>) {
  return useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => api<Project>(`/api/projects/${projectId}`),
    enabled: !!projectId,
    ...options,
  });
}

export function useCreateProject(
  options?: UseMutationOptions<Project, Error, CreateProjectInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<Project>("/api/projects", { method: "POST", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", "list"] });
    },
    ...options,
  });
}

// ---------- Project files ----------

export function useListProjectFiles(
  projectId: string,
  filter?: { prefix?: string },
  options?: UseQueryOptions<ProjectFileList>,
) {
  return useQuery({
    queryKey: qk.projectFiles(projectId, filter?.prefix),
    queryFn: () =>
      api<ProjectFileList>(`/api/projects/${projectId}/files`, {
        query: filter?.prefix ? { prefix: filter.prefix } : undefined,
      }),
    enabled: !!projectId,
    ...options,
  });
}

export interface RestoreCheckpointResult {
  readonly ok: boolean;
  readonly files: number;
  readonly bytes: number;
}

export interface UndoTurnResult {
  readonly ok: boolean;
  readonly files: number;
  readonly bytes: number;
}

export function useUndoTurn(
  projectId: string,
  options?: UseMutationOptions<UndoTurnResult, Error, { turnId: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ turnId }) =>
      api<UndoTurnResult>(`/api/projects/${projectId}/turns/${turnId}/undo`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.projectFiles(projectId) });
      qc.invalidateQueries({ queryKey: ["projects", projectId, "files", "read"] });
    },
    ...options,
  });
}

export function useRestoreCheckpoint(
  projectId: string,
  options?: UseMutationOptions<RestoreCheckpointResult, Error, { turnId: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ turnId }) =>
      api<RestoreCheckpointResult>(
        `/api/projects/${projectId}/checkpoints/${turnId}/restore`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.projectFiles(projectId) });
      // file content cache: just invalidate the lot for this project
      qc.invalidateQueries({ queryKey: ["projects", projectId, "files", "read"] });
    },
    ...options,
  });
}

export function useAuditLog(
  projectId: string,
  options?: Partial<UseQueryOptions<AuditLog>>,
) {
  return useQuery({
    queryKey: ["projects", projectId, "audit"],
    queryFn: () => api<AuditLog>(`/api/projects/${projectId}/audit`),
    enabled: !!projectId,
    staleTime: 15_000,
    ...options,
  });
}

export function useSharedProjects(
  options?: Partial<UseQueryOptions<SharedProjectList>>,
) {
  return useQuery({
    queryKey: ["projects", "shared-with-me"],
    queryFn: () => api<SharedProjectList>("/api/projects/shared-with-me"),
    staleTime: 30_000,
    ...options,
  });
}

export function usePublishedProjects(
  options?: Partial<UseQueryOptions<{ items: ReadonlyArray<PublishedProject> }>>,
) {
  return useQuery({
    queryKey: ["projects", "published"],
    queryFn: () =>
      api<{ items: ReadonlyArray<PublishedProject> }>("/api/projects/published"),
    staleTime: 30_000,
    ...options,
  });
}

export function useProjectMembers(
  projectId: string,
  options?: Partial<UseQueryOptions<ProjectMembersResponse>>,
) {
  return useQuery({
    queryKey: ["projects", projectId, "members"],
    queryFn: () => api<ProjectMembersResponse>(`/api/projects/${projectId}/members`),
    enabled: !!projectId,
    staleTime: 5_000,
    ...options,
  });
}

export function useCreateInvite(
  projectId: string,
  options?: UseMutationOptions<CreateInviteResult, Error, { email: string; role?: "editor" | "viewer" }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<CreateInviteResult>(`/api/projects/${projectId}/invites`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "members"] });
    },
    ...options,
  });
}

export function useRevokeInvite(
  projectId: string,
  options?: UseMutationOptions<void, Error, { inviteId: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inviteId }) =>
      api<void>(`/api/projects/${projectId}/invites/${inviteId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "members"] });
    },
    ...options,
  });
}

export function useRemoveMember(
  projectId: string,
  options?: UseMutationOptions<void, Error, { userId: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }) =>
      api<void>(`/api/projects/${projectId}/members/${userId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "members"] });
    },
    ...options,
  });
}

export function useInvitePreview(token: string | null) {
  return useQuery({
    queryKey: ["invites", token],
    queryFn: () => api<InvitePreview>(`/api/invites/${encodeURIComponent(token!)}`),
    enabled: !!token,
  });
}

export function useAcceptInvite(
  options?: UseMutationOptions<AcceptInviteResult, Error, { token: string }>,
) {
  return useMutation({
    mutationFn: ({ token }) =>
      api<AcceptInviteResult>(`/api/invites/${encodeURIComponent(token)}/accept`, {
        method: "POST",
      }),
    ...options,
  });
}

export interface TurnState {
  readonly running: boolean;
  readonly currentTurnId: string | null;
}

export interface PublishStatus {
  readonly status: "unpublished" | "queued" | "live";
  readonly slug: string | null;
  readonly url: string | null;
  readonly buildHash?: string;
  readonly publishedAt?: string;
  readonly note?: string;
}

export function usePublishStatus(
  projectId: string,
  options?: Partial<UseQueryOptions<PublishStatus>>,
) {
  return useQuery({
    queryKey: ["projects", projectId, "publish"],
    queryFn: () => api<PublishStatus>(`/api/projects/${projectId}/publish`),
    enabled: !!projectId,
    staleTime: 5_000,
    ...options,
  });
}

export function usePublishProject(
  projectId: string,
  options?: UseMutationOptions<PublishStatus, Error, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<PublishStatus>(`/api/projects/${projectId}/publish`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "publish"] });
    },
    ...options,
  });
}

export function useTurnState(
  projectId: string,
  options?: Partial<UseQueryOptions<TurnState>>,
) {
  return useQuery({
    queryKey: ["projects", projectId, "turn-state"],
    queryFn: () => api<TurnState>(`/api/projects/${projectId}/turn-state`),
    enabled: !!projectId,
    refetchInterval: 3_000,
    staleTime: 0,
    ...options,
  });
}

export function useListProjectTurns(
  projectId: string,
  options?: Partial<UseQueryOptions<AiTurnList>>,
) {
  return useQuery({
    queryKey: ["projects", projectId, "turns"],
    queryFn: () => api<AiTurnList>(`/api/projects/${projectId}/turns`),
    enabled: !!projectId,
    staleTime: 10_000,
    ...options,
  });
}

export function useReadProjectFile(
  projectId: string,
  path: string | null,
  options?: UseQueryOptions<ProjectFileContents>,
) {
  return useQuery({
    queryKey: qk.projectFile(projectId, path ?? ""),
    queryFn: () =>
      api<ProjectFileContents>(
        `/api/projects/${projectId}/files/${encodeURI(path!)}`,
      ),
    enabled: !!projectId && !!path,
    staleTime: 5_000,
    ...options,
  });
}

export interface SaveProjectFileResult {
  readonly path: string;
  readonly size: number;
  readonly modifiedAt: string;
}

export function useSaveProjectFile(
  projectId: string,
  options?: UseMutationOptions<SaveProjectFileResult, Error, { path: string; content: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, content }) =>
      api<SaveProjectFileResult>(`/api/projects/${projectId}/files/${encodeURI(path)}`, {
        method: "PUT",
        body: { content },
      }),
    onSuccess: (_data, vars) => {
      // Refresh the tree (size + mtime) and the in-memory copy of the
      // file we just wrote so any other open reader picks up the change.
      qc.invalidateQueries({ queryKey: qk.projectFiles(projectId) });
      qc.setQueryData<ProjectFileContents>(
        qk.projectFile(projectId, vars.path),
        (prev) => prev && { ...prev, content: vars.content },
      );
    },
    ...options,
  });
}

// ---------- Project tasks (Kanban / background queue) ----------

export type ProjectTaskColumn = "draft" | "active" | "ready" | "done";

export interface ProjectTaskRecord {
  readonly id: string;
  readonly projectId: string;
  readonly prompt: string;
  readonly column: ProjectTaskColumn;
  /** Most recent worker error, or null if the last run succeeded /
   * never ran. Surfaced as a chip on Kanban cards. */
  readonly lastError: string | null;
  /** ISO timestamp of the most recent worker run, or null. */
  readonly lastRunAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

interface ProjectTaskList {
  readonly items: ReadonlyArray<ProjectTaskRecord>;
}

export const projectTasksKey = (projectId: string) =>
  ["projects", projectId, "tasks"] as const;

export function useListProjectTasks(
  projectId: string,
  options?: Partial<UseQueryOptions<ProjectTaskList>>,
) {
  return useQuery({
    queryKey: projectTasksKey(projectId),
    queryFn: () => api<ProjectTaskList>(`/api/projects/${projectId}/tasks`),
    enabled: !!projectId,
    staleTime: 5_000,
    ...options,
  });
}

export function useCreateProjectTask(
  projectId: string,
  options?: UseMutationOptions<ProjectTaskRecord, Error, { prompt: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ prompt }) =>
      api<ProjectTaskRecord>(`/api/projects/${projectId}/tasks`, {
        method: "POST",
        body: { prompt },
      }),
    onSuccess: (created) => {
      qc.setQueryData<ProjectTaskList>(projectTasksKey(projectId), (prev) =>
        prev ? { items: [created, ...prev.items] } : { items: [created] },
      );
    },
    ...options,
  });
}

export function useUpdateProjectTask(
  projectId: string,
  options?: UseMutationOptions<
    ProjectTaskRecord,
    Error,
    { id: string; column?: ProjectTaskColumn; prompt?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, column, prompt }) =>
      api<ProjectTaskRecord>(`/api/projects/${projectId}/tasks/${id}`, {
        method: "PATCH",
        body: { ...(column && { column }), ...(prompt && { prompt }) },
      }),
    onSuccess: (updated) => {
      qc.setQueryData<ProjectTaskList>(projectTasksKey(projectId), (prev) =>
        prev
          ? { items: prev.items.map((t) => (t.id === updated.id ? updated : t)) }
          : { items: [updated] },
      );
    },
    ...options,
  });
}

export function useDeleteProjectTask(
  projectId: string,
  options?: UseMutationOptions<void, Error, { id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) =>
      api<void>(`/api/projects/${projectId}/tasks/${id}`, { method: "DELETE" }),
    onSuccess: (_data, vars) => {
      qc.setQueryData<ProjectTaskList>(projectTasksKey(projectId), (prev) =>
        prev ? { items: prev.items.filter((t) => t.id !== vars.id) } : prev,
      );
    },
    ...options,
  });
}

// ---------- Project sandbox (Fly Machines per project) ----------

export type SandboxStatus =
  | "starting"
  | "running"
  | "stopped"
  | "destroying"
  | "destroyed"
  | "error";

export interface SandboxRecord {
  readonly id: string;
  readonly projectId: string;
  readonly flyMachineId: string;
  readonly region: string;
  readonly status: SandboxStatus;
  readonly image: string;
  readonly errorMessage: string | null;
  readonly startedAt: string;
  readonly lastActiveAt: string;
  readonly destroyedAt: string | null;
}

interface SandboxResponse {
  readonly sandbox: SandboxRecord | null;
}

interface StartSandboxResponse {
  readonly sandbox: SandboxRecord;
  readonly alreadyExisted: boolean;
}

export const sandboxKey = (projectId: string) =>
  ["projects", projectId, "sandbox"] as const;

/** Polls the current sandbox state — useful for keeping the UI badge
 * fresh while the machine transitions through starting → running. */
export function useSandbox(
  projectId: string,
  options?: Partial<UseQueryOptions<SandboxResponse>>,
) {
  return useQuery({
    queryKey: sandboxKey(projectId),
    queryFn: () => api<SandboxResponse>(`/api/projects/${projectId}/sandbox`),
    enabled: !!projectId,
    staleTime: 5_000,
    refetchInterval: 10_000,
    ...options,
  });
}

export function useStartSandbox(
  projectId: string,
  options?: UseMutationOptions<StartSandboxResponse, Error, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<StartSandboxResponse>(`/api/projects/${projectId}/sandbox/start`, { method: "POST" }),
    onSuccess: (res) => {
      qc.setQueryData<SandboxResponse>(sandboxKey(projectId), { sandbox: res.sandbox });
    },
    ...options,
  });
}

export function useStopSandbox(
  projectId: string,
  options?: UseMutationOptions<SandboxResponse, Error, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<SandboxResponse>(`/api/projects/${projectId}/sandbox/stop`, { method: "POST" }),
    onSuccess: (res) => {
      qc.setQueryData<SandboxResponse>(sandboxKey(projectId), res);
    },
    ...options,
  });
}

export function useDestroySandbox(
  projectId: string,
  options?: UseMutationOptions<void, Error, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<void>(`/api/projects/${projectId}/sandbox`, { method: "DELETE" }),
    onSuccess: () => {
      qc.setQueryData<SandboxResponse>(sandboxKey(projectId), { sandbox: null });
    },
    ...options,
  });
}

export interface SandboxExecResult {
  readonly stdout: string;
  readonly stderr: string;
  readonly exitCode: number;
}

export function useSandboxExec(
  projectId: string,
  options?: UseMutationOptions<SandboxExecResult, Error, { command: string; timeoutMs?: number }>,
) {
  return useMutation({
    mutationFn: ({ command, timeoutMs }) =>
      api<SandboxExecResult>(`/api/projects/${projectId}/sandbox/exec`, {
        method: "POST",
        body: { command, ...(timeoutMs && { timeoutMs }) },
      }),
    ...options,
  });
}

// ---------- EAS Build ----------

export type EasBuildPlatform = "ios" | "android";
export type EasBuildStatus =
  | "new"
  | "in-queue"
  | "in-progress"
  | "errored"
  | "finished"
  | "canceled"
  | string;

export interface EasBuildRecord {
  readonly id: string;
  readonly expoBuildId: string;
  readonly platform: EasBuildPlatform | string;
  readonly status: EasBuildStatus;
  readonly artifactUrl: string | null;
  readonly createdAt: string;
  readonly completedAt: string | null;
}

interface BuildsList {
  readonly items: ReadonlyArray<EasBuildRecord>;
}

interface BuildResponse {
  readonly build: EasBuildRecord;
  readonly stale?: boolean;
}

export const buildsKey = (projectId: string) => ["projects", projectId, "builds"] as const;

/** Polls the project's build history. Refetches every 10s so in-progress
 *  builds tick toward "finished" without manual refresh. */
export function useProjectBuilds(
  projectId: string,
  options?: Partial<UseQueryOptions<BuildsList>>,
) {
  return useQuery({
    queryKey: buildsKey(projectId),
    queryFn: () => api<BuildsList>(`/api/projects/${projectId}/builds`),
    enabled: !!projectId,
    staleTime: 5_000,
    refetchInterval: 10_000,
    ...options,
  });
}

export function useStartBuild(
  projectId: string,
  options?: UseMutationOptions<BuildResponse, Error, { platform: EasBuildPlatform; profile?: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ platform, profile }) =>
      api<BuildResponse>(`/api/projects/${projectId}/builds`, {
        method: "POST",
        body: { platform, ...(profile && { profile }) },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buildsKey(projectId) });
    },
    ...options,
  });
}

/** Refreshes a single build's status against EAS. Cheap to call on click. */
export function useRefreshBuild(
  projectId: string,
  options?: UseMutationOptions<BuildResponse, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (buildId) =>
      api<BuildResponse>(`/api/projects/${projectId}/builds/${buildId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buildsKey(projectId) });
    },
    ...options,
  });
}

// ---------- Live sandbox introspection ----------

/**
 * useQuery wrapper around POST /sandbox/exec. Use this for *periodic*
 * sandbox reads (port table, /proc/* stats) where you want React Query
 * caching, deduplication, and a refetchInterval. For one-shot user-
 * triggered execs (run a script, run grep), keep using useSandboxExec.
 *
 * Returns {stdout, stderr, exitCode} unchanged from the exec endpoint.
 */
export function useLiveSandboxExec(
  projectId: string,
  command: string,
  options?: Partial<UseQueryOptions<SandboxExecResult>> & {
    /** Skip the call entirely. Useful when the sandbox isn't running. */
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: ["sandbox", projectId, "live-exec", command] as const,
    queryFn: () =>
      api<SandboxExecResult>(`/api/projects/${projectId}/sandbox/exec`, {
        method: "POST",
        body: { command },
      }),
    enabled: !!projectId && !!command && (options?.enabled ?? true),
    staleTime: 2_000,
    refetchInterval: 5_000,
    ...options,
  });
}
